'use client';

import type { ComponentProps, ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import { isUnattributedOutsideInteraction } from '@/components/attributes';
import { cn } from '@/lib/utils';

/**
 * Shared right-side detail Sheet wrapper.
 *
 * Captures the layout pattern used across the dashboard's full-height detail
 * panels (discovery institution sheet, title-search review sheet, future
 * matter/contact detail sheets). The shell is:
 *
 *   - Right-side `Sheet` from shadcn/ui.
 *   - 85vw width, gray-50 body background, no padding (consumers own padding
 *     in the body slot).
 *   - Fixed header row: icon slot + title + description on the left, action
 *     slot on the right. The row reserves a right-hand gutter for the
 *     built-in close (X) button whenever that button is shown, so the action
 *     slot never runs underneath it (LF-5977).
 *   - Optional banner row below the header (e.g. concierge / cancelled-title /
 *     PDF-only banner).
 *   - Body slot fills the remaining height.
 *
 * Title-search and discovery share this wrapper to keep the column widths +
 * header pattern identical. Future right-side detail panels in this project
 * should also adopt it.
 *
 * Behaviour is purely structural — no business logic, no data fetching, no
 * state ownership beyond what the consumer passes through `open` /
 * `onOpenChange`.
 */
export interface RightDetailSheetProps {
  /** Open state of the underlying `Sheet`. */
  open: boolean;
  /** Open-state callback. */
  onOpenChange: (open: boolean) => void;
  /**
   * Visual marker on the left of the header (typically an icon in a coloured
   * tile, e.g. `InstitutionIcon`, `FileSearch`). May be omitted; the header
   * collapses gracefully.
   */
  icon?: ReactNode;
  /**
   * Header title — rendered inside `SheetTitle` for accessibility. Pass a
   * `string` to use default typography, or any `ReactNode` for custom
   * layout (e.g. badge inline with the title text).
   */
  title: ReactNode;
  /**
   * Optional header description rendered inside `SheetDescription`. Pass any
   * `ReactNode`.
   */
  description?: ReactNode;
  /**
   * Right-side action slot in the header row (typically a button — e.g.
   * `CommunicationsDeepLinkButton`, an external-link CTA, a status badge).
   */
  headerActions?: ReactNode;
  /**
   * Optional banner row rendered below the header, above the body. Use for
   * persistent advisory states (concierge, cancelled-title, PDF-only, missing
   * SRN). The wrapper draws no border around this slot — the banner owns its
   * own visual treatment.
   */
  banner?: ReactNode;
  /** Body content. Fills the remaining height. Consumer owns padding. */
  children: ReactNode;
  /** Optional className on the `SheetContent` for ad-hoc tweaks. */
  className?: string;
  /** Optional className on the body wrapper for ad-hoc tweaks. */
  bodyClassName?: string;
  /** `data-testid` passed to `SheetContent` for stable selectors. */
  contentTestId?: string;
  /**
   * Whether to show the built-in top-right close (X) button. When `true` the
   * header row reserves a right-hand gutter so `headerActions` clears the X;
   * when `false` the header keeps its full width.
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Optional interception for the built-in outside-click / Escape dismiss —
   * passed straight through to the underlying `SheetContent`. Call
   * `event.preventDefault()` to keep the sheet open (e.g. an unsaved-changes
   * guard), matching the pattern in `add-entitlement-modal.tsx`. Omitted by
   * consumers with nothing to guard.
   */
  onPointerDownOutside?: ComponentProps<
    typeof SheetContent
  >['onPointerDownOutside'];
  onEscapeKeyDown?: ComponentProps<typeof SheetContent>['onEscapeKeyDown'];
  /**
   * Lets a caller take the outside-interaction decision away from the shell's
   * own default (VALE-1677) — passed straight through to `SheetContent`, ahead
   * of {@link isUnattributedOutsideInteraction}. See this component's own
   * `onInteractOutside` wiring for why that default exists and why it is
   * unconditional rather than opt-in.
   */
  onInteractOutside?: ComponentProps<typeof SheetContent>['onInteractOutside'];
}

export function RightDetailSheet({
  open,
  onOpenChange,
  icon,
  title,
  description,
  headerActions,
  banner,
  children,
  className,
  bodyClassName,
  contentTestId,
  showCloseButton = true,
  onPointerDownOutside,
  onInteractOutside,
  onEscapeKeyDown
}: RightDetailSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid={contentTestId}
        side="right"
        showCloseButton={showCloseButton}
        className={cn(
          'bg-surface-subtle flex h-full w-full max-w-[85vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[85vw]',
          className
        )}
        onOpenAutoFocus={(e) => {
          // Radix otherwise auto-focuses the first tabbable descendant (e.g.
          // a header action or the download button in the correspondence
          // panel), which fires that element's focus-triggered Tooltip and
          // scrolls it into view mid slide-in transition (LF-4617).
          e.preventDefault();
        }}
        onPointerDownOutside={onPointerDownOutside}
        /*
          **Refuse an outside-interaction verdict reached with no attribution**
          (VALE-1677, extending VALE-1652 off `ai-import-dialog-content.tsx`).
          Every caller of this shell can host the attribute treatment's
          composite popovers, whose capture-phase commit can unmount the
          pointerdown target before Radix's own dismissable layer looks at it —
          see `isUnattributedOutsideInteraction`'s own doc for the mechanism.
          Baked in here rather than left to each caller, per
          `components/attributes/CLAUDE.md` §6d: "the surface nobody
          reproduces against cannot be the one left broken." The caller runs
          first, so a mount can still take the decision away from the shell.
        */
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          if (event.defaultPrevented) return;
          if (isUnattributedOutsideInteraction(event)) event.preventDefault();
        }}
        onEscapeKeyDown={onEscapeKeyDown}
      >
        <SheetHeader
          data-testid="right-detail-sheet-header"
          className={cn(
            'bg-card border-b py-4 pl-6',
            // Radix pins the close (X) button at `right-4` and it is `size-4`
            // wide, so it occupies the 13.6px–27.2px band from the sheet's
            // right edge (this app re-bases `--spacing` to 0.2125rem/3.4px).
            // A plain `pr-6` stops the header's content box at 20.4px, which
            // runs the actions slot ~7px underneath the X. Reserve the gutter
            // — but only when the close button is actually rendered, so a
            // consumer that suppresses it keeps the full header width
            // (LF-5977).
            showCloseButton ? 'pr-12' : 'pr-6'
          )}
        >
          <div className="flex items-center gap-3">
            {icon ? <div className="shrink-0">{icon}</div> : null}
            <div className="min-w-0 flex-1">
              {/* 16px semibold, not `SheetTitle`'s default 18px medium. A
                  detail drawer's title names a record the reader already chose
                  — it is a label, not a page heading — and at `text-lg` it
                  outweighed everything under it. The templates detail drawer
                  has always been `text-base font-semibold`; this brings the
                  shared shell to the same treatment rather than leaving the
                  app's two drawer families a step apart. Set here rather than
                  on `SheetTitle` itself, which every dialog in the app uses. */}
              <SheetTitle className="text-base font-semibold">
                {title}
              </SheetTitle>
              {description ? (
                <SheetDescription>{description}</SheetDescription>
              ) : null}
            </div>
            {headerActions ? (
              <div
                data-testid="right-detail-sheet-header-actions"
                className="shrink-0"
              >
                {headerActions}
              </div>
            ) : null}
          </div>
        </SheetHeader>

        {banner ? <div className="shrink-0">{banner}</div> : null}

        <div className={cn('flex min-h-0 flex-1 flex-col', bodyClassName)}>
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
